import type { Mat3 } from '../core/math';
import type { Point } from '../core/types';

/**
 * Maps the unit quad onto one brush stamp in the clip space of a document-sized
 * target: the quad centre lands on `centre`, its axes become `width` and
 * `height` document pixels, and the whole thing rotates by `angle` degrees.
 *
 * Targets are drawn without a Y flip (see `QUAD_TO_TARGET`), so a positive angle
 * turns clockwise on screen.
 */
export function stampMatrix(
  centre: Point,
  width: number,
  height: number,
  angle: number,
  targetWidth: number,
  targetHeight: number,
): Mat3 {
  const radians = (angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const sx = 2 / targetWidth;
  const sy = 2 / targetHeight;

  // Corner offset that puts the quad's centre, not its origin, on `centre`.
  const originX = centre.x - 0.5 * (width * cos - height * sin);
  const originY = centre.y - 0.5 * (width * sin + height * cos);

  return new Float32Array([
    width * cos * sx,
    width * sin * sy,
    0,
    -height * sin * sx,
    height * cos * sy,
    0,
    originX * sx - 1,
    originY * sy - 1,
    1,
  ]);
}

/** Axis-aligned box a rotated stamp covers, padded for antialiasing. */
export function stampBounds(
  centre: Point,
  width: number,
  height: number,
  angle: number,
  padding = 2,
): { x: number; y: number; width: number; height: number } {
  const radians = (angle * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const extentX = (width * cos + height * sin) / 2 + padding;
  const extentY = (width * sin + height * cos) / 2 + padding;
  return {
    x: centre.x - extentX,
    y: centre.y - extentY,
    width: extentX * 2,
    height: extentY * 2,
  };
}
